import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  Scissors,
  Cloud,
  Globe,
  Crosshair,
  Crop,
  Wand2,
  BookmarkCheck,
  Layers,
  Star,
  History,
  ChevronDown,
} from "lucide-react";

import { LANGS, LANG_LABELS, LANG_FLAGS, detectLang, t } from "./i18n";

const TG_BOT = "https://t.me/Clipra_bot";

// 11 capabilities — 8 LIVE + 3 PRO. No SOON placeholders, no broken
// promises (priority queue removed in iter17 PO audit; recut merged
// into the new Smart Refine card).
const FEATURES = [
  { id: "download", Icon: Download, group: "live" },
  { id: "cut", Icon: Scissors, group: "live" },
  { id: "miniapp", Icon: Layers, group: "live" },
  { id: "refine", Icon: Wand2, group: "live" },
  { id: "library", Icon: BookmarkCheck, group: "live" },
  { id: "history", Icon: History, group: "live" },
  { id: "langs", Icon: Globe, group: "live" },
  { id: "large", Icon: Cloud, group: "live" },
  { id: "precise", Icon: Crosshair, group: "pro" },
  { id: "crop", Icon: Crop, group: "pro" },
  { id: "subs", Icon: Star, group: "pro" },
];

export default function Landing() {
  const [lang, setLang] = useState(detectLang);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("clipra.lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const tt = (k) => t(k, lang);

  return (
    <div className="dark bg-background text-foreground min-h-screen overflow-x-hidden" data-testid="landing">
      <Nav lang={lang} setLang={setLang} t={tt} />
      <Hero t={tt} />
      <Features t={tt} />
      <How t={tt} />
      <CTA t={tt} />
      <Footer t={tt} />
    </div>
  );
}

/* ── NAV ────────────────────────────────────────────────────────────── */

function Nav({ lang, setLang, t }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 border-b border-border bg-background/70 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="brand-link" aria-label="Clipra">
          <span className="font-display text-[2.4rem] tracking-tight lowercase leading-none">clipra</span>
          <img
            src="/logos/clipra_icon_invert.png"
            alt=""
            aria-hidden="true"
            className="h-12 w-auto"
            data-testid="brand-logo"
          />
        </a>
        <div className="flex items-center gap-1.5">
          <div className="relative">
            <button
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground border border-transparent hover:border-border transition-colors"
              data-testid="lang-toggle"
              aria-label={t("nav.lang")}
            >
              <span>{LANG_FLAGS[lang]}</span>
              <span>{lang.toUpperCase()}</span>
              <ChevronDown className="w-3 h-3" />
            </button>
            {open && (
              <div
                className="absolute right-0 mt-1 w-44 border border-border bg-popover py-1 shadow-2xl"
                data-testid="lang-menu"
              >
                {LANGS.map((code) => (
                  <button
                    key={code}
                    onClick={() => {
                      setLang(code);
                      setOpen(false);
                    }}
                    data-testid={`lang-opt-${code}`}
                    className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-secondary/40 ${
                      code === lang ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    <span>{LANG_FLAGS[code]}</span>
                    <span>{LANG_LABELS[code]}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/admin/login"
            data-testid="nav-admin"
            className="px-3 py-1.5 text-xs font-mono uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground border border-transparent hover:border-border transition-colors"
          >
            {t("nav.admin")}
          </Link>
          <a
            href={TG_BOT}
            data-testid="nav-tg"
            className="ml-2 hidden sm:inline-block px-3 py-1.5 text-xs font-mono uppercase tracking-[0.16em] bg-foreground text-background hover:opacity-90 transition-opacity"
          >
            {t("nav.openBot")}
          </a>
        </div>
      </div>
    </header>
  );
}

/* ── HERO ───────────────────────────────────────────────────────────── */

function Hero({ t }) {
  // Animated word cycle in the title's last line: "Telegram-bot." ⇄
  // "Telegram-app." (suffix baked in so the period sticks to the word
  // and doesn't drift when the shorter alternative is on screen).
  const suffix = t("hero.title.line2_suffix");
  const words = [
    t("hero.title.swap_a") + suffix,
    t("hero.title.swap_b") + suffix,
  ];
  // Single monotonic counter — every tick triggers a new "drop": the
  // previous word slides DOWN out the bottom, the next word slides DOWN
  // in from the top. Always one direction (top → bottom). React's `key`
  // forces fresh DOM nodes per tick so the keyframe animation re-runs.
  const [tick, setTick] = useState(0);
  const activeIdx = tick % words.length;
  const prevIdx = tick === 0 ? null : (tick - 1) % words.length;
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden">
      {/* Cinematic ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-red-500/[0.05] blur-3xl" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[400px] rounded-full bg-emerald-500/[0.04] blur-3xl" />
      </div>
      {/* Filmstrip top frame */}
      <div className="filmstrip-divider mt-px" />
      <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 sm:pt-32 sm:pb-40">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          {t("hero.eyebrow")}
        </div>
        <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
          <span
            className="block opacity-0 animate-[fadein_0.7s_ease-out_forwards]"
            style={{ animationDelay: "0.05s" }}
          >
            {t("hero.title.line1")}
          </span>
          <span
            className="block opacity-0 animate-[fadein_0.7s_ease-out_forwards]"
            style={{ animationDelay: "0.17s" }}
            data-testid="hero-title-line2"
          >
            {t("hero.title.line2_prefix")}
            <span className="relative inline-block align-baseline overflow-hidden">
              {/* Invisible widest-word spacer reserves layout width AND
                  height so the slider clips cleanly without reflowing
                  surrounding text. */}
              <span className="invisible block" aria-hidden="true">
                {words.reduce((a, b) => (a.length >= b.length ? a : b), "")}
              </span>
              {/* Outgoing word — only rendered after the first tick.
                  Slides DOWN out the bottom. Re-keyed on every tick so
                  the keyframe re-runs from scratch. */}
              {prevIdx !== null && (
                <span
                  key={`out-${tick}`}
                  data-testid="hero-swap-out"
                  className="absolute inset-x-0 top-0 text-emerald-400 will-change-transform whitespace-nowrap"
                  style={{
                    animation: "slide-down-out 700ms cubic-bezier(0.4,0,0.2,1) forwards",
                  }}
                  aria-hidden="true"
                >
                  {words[prevIdx]}
                </span>
              )}
              {/* Incoming word — slides DOWN in from above. On the very
                  first render (tick=0) it sits at translateY(0) without
                  animating, so the page loads with the title intact. */}
              <span
                key={`in-${tick}`}
                data-testid={`hero-swap-word-${activeIdx}`}
                className="absolute inset-x-0 top-0 text-emerald-400 will-change-transform whitespace-nowrap"
                style={{
                  animation:
                    tick === 0
                      ? "none"
                      : "slide-down-in 700ms cubic-bezier(0.4,0,0.2,1) forwards",
                }}
              >
                {words[activeIdx]}
              </span>
            </span>
          </span>
        </h1>
        <p
          className="mt-7 max-w-2xl text-base sm:text-lg text-muted-foreground leading-relaxed opacity-0 animate-[fadein_0.7s_ease-out_forwards]"
          style={{ animationDelay: "0.45s" }}
        >
          {t("hero.subtitle")}
        </p>
        <div
          className="mt-10 flex flex-wrap gap-2 opacity-0 animate-[fadein_0.7s_ease-out_forwards]"
          style={{ animationDelay: "0.6s" }}
        >
          <a
            href={TG_BOT}
            data-testid="hero-cta-primary"
            className="px-5 py-3 text-sm font-medium bg-foreground text-background hover:opacity-90 active:translate-y-px transition-all"
          >
            {t("hero.cta.primary")}
          </a>
          <a
            href="#how"
            data-testid="hero-cta-secondary"
            className="px-5 py-3 text-sm font-medium border border-border hover:bg-secondary/40 transition-colors"
          >
            {t("hero.cta.secondary")}
          </a>
        </div>
      </div>
      <div className="filmstrip-divider" />

      {/* keyframes — define here once */}
      <style>{`
        @keyframes fadein {from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes slide-down-in {from{transform:translateY(-100%)}to{transform:translateY(0)}}
        @keyframes slide-down-out {from{transform:translateY(0)}to{transform:translateY(100%)}}
      `}</style>
    </section>
  );
}

/* ── FEATURES ───────────────────────────────────────────────────────── */

function Features({ t }) {
  return (
    <section className="max-w-6xl mx-auto px-6 py-24" data-testid="features">
      <div className="max-w-2xl">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          // 01 · capabilities
        </div>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight">
          {t("feat.title")}
        </h2>
        {t("feat.subtitle") && (
          <p className="mt-3 text-muted-foreground text-base">{t("feat.subtitle")}</p>
        )}
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
        {FEATURES.map(({ id, Icon, group }) => (
          <div
            key={id}
            className="bg-background p-6 hover:bg-secondary/20 transition-colors"
            data-testid={`feat-${id}`}
          >
            <div className="flex items-start justify-between">
              <Icon className="w-5 h-5 text-foreground" strokeWidth={1.5} />
              <Tag group={group} t={t} />
            </div>
            <div className="mt-6 font-display text-lg leading-snug">
              {t(`feat.${id}.title`)}
            </div>
            <div className="mt-2 text-sm text-muted-foreground leading-relaxed">
              {t(`feat.${id}.body`)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function Tag({ group, t }) {
  const styles = {
    live: "border-emerald-500/40 text-emerald-400",
    pro: "border-violet-500/40 text-violet-400",
    soon: "border-amber-500/40 text-amber-400",
  };
  return (
    <span
      className={`text-[9px] font-mono uppercase tracking-[0.22em] px-1.5 py-0.5 border ${styles[group]}`}
    >
      {t(`tag.${group}`)}
    </span>
  );
}

/* ── HOW IT WORKS ───────────────────────────────────────────────────── */

function How({ t }) {
  return (
    <section
      id="how"
      className="border-t border-border bg-secondary/[0.04]"
      data-testid="how-section"
    >
      <div className="max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-2xl">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
            // 02 · workflow
          </div>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl tracking-tight">
            {t("how.title")}
          </h2>
        </div>
        <ol className="mt-12 grid gap-px bg-border md:grid-cols-3">
          {[1, 2, 3].map((n) => (
            <li
              key={n}
              className="bg-background p-8 relative"
              data-testid={`how-step-${n}`}
            >
              <div className="absolute top-6 right-6 font-mono text-5xl text-muted-foreground/20 leading-none">
                {String(n).padStart(2, "0")}
              </div>
              <div className="font-display text-xl">
                {t(`how.step${n}.title`)}
              </div>
              <div className="mt-3 text-sm text-muted-foreground leading-relaxed">
                {t(`how.step${n}.body`)}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ── CTA ────────────────────────────────────────────────────────────── */

function CTA({ t }) {
  return (
    <section className="border-t border-border" data-testid="cta-section">
      <div className="max-w-3xl mx-auto px-6 py-28 text-center">
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight">
          {t("cta.title")}
        </h2>
        <p className="mt-5 text-base text-muted-foreground">{t("cta.body")}</p>
        <a
          href={TG_BOT}
          data-testid="cta-button"
          className="inline-block mt-10 px-7 py-3.5 text-sm font-medium bg-foreground text-background hover:opacity-90 active:translate-y-px transition-all"
        >
          {t("cta.button")} →
        </a>
      </div>
    </section>
  );
}

/* ── FOOTER ─────────────────────────────────────────────────────────── */

function Footer({ t }) {
  return (
    <footer className="border-t border-border" data-testid="landing-footer">
      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-display text-[2rem] lowercase leading-none">clipra</span>
            <img
              src="/logos/clipra_icon_invert.png"
              alt=""
              aria-hidden="true"
              className="h-10 w-auto"
              data-testid="footer-logo"
            />
          </div>
          <div className="text-xs font-mono text-muted-foreground mt-2">
            {t("foot.tagline")}
          </div>
        </div>
        <div className="flex flex-col sm:items-end gap-2 self-end">
          <Link
            to="/terms"
            className="text-xs font-mono text-muted-foreground hover:text-foreground transition-colors"
            data-testid="footer-terms-link"
          >
            {t("foot.terms")}
          </Link>
          <div className="text-xs font-mono text-muted-foreground">
            {t("foot.copyright")}
          </div>
        </div>
      </div>
    </footer>
  );
}
